import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type CallbackType = 'verify-email' | 'magic-link';
type Status = 'loading' | 'success' | 'error';

interface AuthCallbackPageProps {
  type: CallbackType;
}

/**
 * Handles authentication callbacks for:
 * - Email verification (/auth/verify-email?token=xxx)
 * - Magic link login (/auth/magic-link?token=xxx)
 */
const AuthCallbackPage: React.FC<AuthCallbackPageProps> = ({ type }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, verifyMagicLink } = useAuth();

  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState('');

  // Track if verification has been attempted to prevent double-execution
  // This is necessary because auth state changes cause function references to update,
  // which would otherwise trigger the useEffect again
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    // Prevent double-execution when auth state changes trigger re-render
    if (hasAttemptedRef.current) return;

    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setError('Invalid link - no token provided');
      return;
    }

    // Mark as attempted before async operation
    hasAttemptedRef.current = true;

    const handleVerification = async () => {
      try {
        if (type === 'verify-email') {
          await verifyEmail(token);
        } else if (type === 'magic-link') {
          await verifyMagicLink(token);
        }
        setStatus('success');
        // Redirect to home after 2 seconds
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } catch (err) {
        setStatus('error');
        const apiError = err as { message?: string };
        setError(apiError.message || 'Verification failed');
      }
    };

    handleVerification();
  }, [searchParams, type, verifyEmail, verifyMagicLink, navigate]);

  const title = type === 'verify-email' ? 'Email Verification' : 'Sign In';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl p-8 border border-gray-700 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-6 animate-spin" />
              <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
              <p className="text-gray-400">
                {type === 'verify-email' ? 'Verifying your email...' : 'Signing you in...'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {type === 'verify-email' ? 'Email Verified!' : 'Welcome!'}
              </h2>
              <p className="text-gray-400 mb-6">
                {type === 'verify-email'
                  ? 'Your email has been verified. Redirecting...'
                  : 'You are now signed in. Redirecting...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2">
                {type === 'verify-email' ? 'Verification Failed' : 'Sign In Failed'}
              </h2>
              <p className="text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Go to Home
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallbackPage;
